﻿import type { CounterAttackConfig, ExerciseCard, GameConfig } from './types';

export const ASSETS = {
  enemyIdle: '/assets/optimized/enemy-slouch-imp-idle-384.webp',
  enemyHit: '/assets/optimized/enemy-slouch-imp-hit-384.webp',
  enemyDefeated: '/assets/optimized/enemy-slouch-imp-defeated-384.webp',
  playerAction: '/assets/optimized/player-phone-action-512.webp',
  battleBg: '/assets/optimized/battle-bg-720.webp',
  resultBadge: '/assets/optimized/result-badge-384.webp',
  appIcon: '/assets/optimized/app-icon-128.webp'
} as const;

export const ART_ASSETS = {
  heroAttack: '/assets/optimized/art-hero-attack.webp',
  heroStanding: '/assets/optimized/art-hero-standing.webp',
  tutorialJumpPhone: '/assets/optimized/tutorial-jump-phone.webp',
  tutorials: {
    jump_rope: '/assets/optimized/tutorial-jump-rope.webp',
    squat: '/assets/optimized/tutorial-squat.webp',
    boxing: '/assets/optimized/tutorial-boxing.webp',
    jumping_jacks: '/assets/optimized/tutorial-jumping-jacks.webp',
    running: '/assets/optimized/tutorial-running.webp'
  },
  monsterAngry: '/assets/optimized/art-monster-angry.webp',
  monsterHit: '/assets/optimized/art-monster-hit.webp',
  monsterDefeated: '/assets/optimized/art-monster-defeated.webp',
  officeMonsters: {
    100: {
      idle: '/assets/optimized/office-monster-100-idle.webp',
      hit: '/assets/optimized/office-monster-100-hit.webp',
      defeated: '/assets/optimized/office-monster-100-defeated.webp'
    },
    300: {
      idle: '/assets/optimized/office-monster-300-idle.webp',
      hit: '/assets/optimized/office-monster-300-hit.webp',
      defeated: '/assets/optimized/office-monster-300-defeated.webp'
    },
    1000: {
      idle: '/assets/optimized/office-monster-1000-idle.webp',
      hit: '/assets/optimized/office-monster-1000-hit.webp',
      defeated: '/assets/optimized/office-monster-1000-defeated.webp'
    }
  },
  iconJumpRope: '/assets/optimized/art-icon-jump-rope.webp',
  iconSquat: '/assets/optimized/art-icon-squat.webp',
  iconBoxing: '/assets/optimized/art-icon-boxing.webp',
  exerciseIcons: {
    jump_rope: '/assets/optimized/exercise-icon-jump-rope.webp',
    squat: '/assets/optimized/exercise-icon-squat.webp',
    boxing: '/assets/optimized/exercise-icon-boxing.webp',
    jumping_jacks: '/assets/optimized/exercise-icon-jumping-jacks.webp',
    running: '/assets/optimized/exercise-icon-running.webp'
  },
  exerciseActions: {
    jump_rope: '/assets/optimized/exercise-action-jump-rope.webp',
    squat: '/assets/optimized/exercise-action-squat.webp',
    boxing: '/assets/optimized/exercise-action-boxing.webp',
    jumping_jacks: '/assets/optimized/exercise-action-jumping-jacks.webp',
    running: '/assets/optimized/exercise-action-running.webp'
  },
  outfits: {
    teal: '/assets/optimized/outfit-teal.webp',
    sunny: '/assets/optimized/outfit-sunny.webp',
    coral: '/assets/optimized/outfit-coral.webp',
    legendary: '/assets/optimized/outfit-legendary.webp'
  }
} as const;


export const EXERCISES: ExerciseCard[] = [
  {
    id: 'jump_rope',
    title: 'Jump-Rope Battle',
    subtitle: 'Grip your phone and defeat the Lazy Monster in 100 reps',
    enabled: true,
    badge: 'Playable MVP',
    accent: '#ffd166',
    assetPath: '/assets/generated/card-jump-rope.png'
  },
  {
    id: 'squat',
    title: 'Squat Challenge',
    subtitle: 'Future version: detect squat rhythm from vertical movement',
    enabled: false,
    badge: 'Coming soon',
    accent: '#84dcc6',
    assetPath: '/assets/generated/card-squat-locked.png',
    disabledReason: 'The MVP only enables jump rope to avoid shipping multiple unstable detectors at once.'
  },
  {
    id: 'boxing',
    title: 'Shadow Boxing',
    subtitle: 'Future version: detect punch rhythm and combos',
    enabled: false,
    badge: 'Coming soon',
    accent: '#ff7a7a',
    assetPath: '/assets/generated/card-shadow-boxing-locked.png',
    disabledReason: 'Requires separate tuning and safety guidance, so it is not available yet.'
  }
];

export const DEFAULT_GAME_CONFIG: GameConfig = {
  targetJumps: 100,
  enemyMaxHp: 100,
  playerBaseAttack: 1,
  hapticEvery: 1,
  minValidIntervalMs: 280,
  maxValidIntervalMs: 1200
};

export const DEFAULT_COUNTER_ATTACK_CONFIG: CounterAttackConfig = {
  firstThresholdRange: [0.6, 0.7],
  secondThresholdRange: [0.25, 0.35],
  countdownStepMs: 1000,
  dodgeWindowMs: 1000,
  monsterHealRate: 0.1,
  dodgeRewardBonusRate: 0.05,
  dodgeDetection: {
    accelThreshold: 3.2,
    gyroThreshold: 260,
    dominantAxisRatio: 1.15
  }
};

export const MIN_ROUND_TIME_MS = 8000;

export const BATTLE_ASSETS = [
  ASSETS.battleBg,
  ASSETS.enemyIdle,
  ASSETS.enemyHit,
  ASSETS.enemyDefeated,
  ASSETS.playerAction,
  ASSETS.resultBadge,
  ART_ASSETS.heroAttack,
  ART_ASSETS.heroStanding,
  ART_ASSETS.tutorialJumpPhone,
  ...Object.values(ART_ASSETS.tutorials),
  ART_ASSETS.monsterAngry,
  ART_ASSETS.monsterHit,
  ART_ASSETS.monsterDefeated,
  ART_ASSETS.iconJumpRope,
  ART_ASSETS.iconSquat,
  ART_ASSETS.iconBoxing,
  ...Object.values(ART_ASSETS.exerciseIcons),
  ...Object.values(ART_ASSETS.exerciseActions),
  ...Object.values(ART_ASSETS.outfits),
  ...Object.values(ART_ASSETS.officeMonsters).flatMap((poses) => Object.values(poses))
];

export function preloadImages(paths: readonly string[]): Promise<void> {
  if (typeof Image === 'undefined') return Promise.resolve();
  return Promise.all(
    paths.map(
      (path) =>
        new Promise<void>((resolve) => {
          const image = new Image();
          image.decoding = 'async';
          image.onload = () => resolve();
          image.onerror = () => resolve();
          image.src = path;
        })
    )
  ).then(() => undefined);
}

